/**
 * ProductGrid - Lista de produtos disponíveis
 * Exibe produtos filtrados em formato de lista compacta
 * 
 * Versão: 1.2.0
 * - Alterado de grid de cards para lista compacta
 * - Adicionado filtro por tabela de preço
 * - v1.2.0: Scroll horizontal interno + min-width no wrapper das linhas
 *   para suportar as novas colunas Unidade/Mensalidade/Prazo sem cortar
 */
import React from 'react';
import { Package, SearchX } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ProductListRow } from './ProductListRow';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useCrmPriceTableItems } from '@/hooks/useCrmPriceTables';

// Props do componente
interface ProductGridProps {
  /** ID da categoria ou array de IDs (para incluir subcategorias) */
  categoryId: string | string[] | null;
  productType: 'all' | 'product' | 'service';
  searchQuery: string;
  priceTableId: string | null;
  showOnlyTableProducts: boolean;
  dealItems: Array<{ product_id: string; quantity: number }>;
  currency: string;
  onAddItem: (productId: string, quantity: number, unitPrice: number) => void;
  onUpdateItemQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  /** ID do workspace para filtrar produtos */
  workspaceId?: string | null;
  /**
   * Quando true: força filtro unit_type='recorrente' (apenas serviços
   * recorrentes) e instrui a linha a usar recurring_price como preço unitário
   * ao adicionar. Usado pelo modal Nova Recorrência (Financeiro).
   */
  onlyRecurring?: boolean;
}

/**
 * Lista de produtos com filtros aplicados
 */
export function ProductGrid({
  categoryId,
  productType,
  searchQuery,
  priceTableId,
  showOnlyTableProducts,
  dealItems,
  currency,
  onAddItem,
  onUpdateItemQuantity,
  onRemoveItem,
  workspaceId,
  onlyRecurring = false,
}: ProductGridProps) {
  // Buscar produtos com filtros (incluindo workspace e unit_type se aplicável)
  const { products, isLoading } = useCrmProducts({
    category_id: categoryId,
    product_type: productType === 'all' ? null : productType,
    search: searchQuery || undefined,
    is_active: true,
    workspace_id: workspaceId,
    // v1.3.0: quando onlyRecurring, restringe a serviços recorrentes via RPC
    unit_type: onlyRecurring ? 'recorrente' : null,
  });


  // Buscar itens da tabela de preço (se selecionada)
  const { items: priceTableItems } = useCrmPriceTableItems(priceTableId || '');

  // Criar mapa de preços da tabela
  const priceTableMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    priceTableItems.forEach((item) => {
      map[item.product_id] = item.price;
    });
    return map;
  }, [priceTableItems]);

  // Criar mapa de itens do deal
  const dealItemsMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    dealItems.forEach((item) => {
      map[item.product_id] = item.quantity;
    });
    return map;
  }, [dealItems]);

  // IDs dos produtos na tabela de preço
  const tableProductIds = React.useMemo(() => {
    return new Set(priceTableItems.map(item => item.product_id));
  }, [priceTableItems]);

  // Filtrar produtos
  const filteredProducts = React.useMemo(() => {
    let result = products;
    
    // Filtrar por tabela de preço se ativo
    if (showOnlyTableProducts && priceTableId && tableProductIds.size > 0) {
      result = result.filter(p => tableProductIds.has(p.id));
    }
    
    return result;
  }, [products, showOnlyTableProducts, priceTableId, tableProductIds]);

  // Estado de loading
  if (isLoading) {
    return (
      <div className="flex-1 p-3">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  // Estado vazio
  if (filteredProducts.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="p-4 rounded-full bg-muted/50 mb-4">
          {searchQuery || showOnlyTableProducts ? (
            <SearchX className="h-8 w-8 text-muted-foreground" />
          ) : (
            <Package className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <h4 className="text-sm font-medium mb-1">
          {showOnlyTableProducts && priceTableId
            ? 'Nenhum produto nesta tabela de preço'
            : searchQuery 
              ? 'Nenhum produto encontrado' 
              : 'Nenhum produto disponível'}
        </h4>
        <p className="text-xs text-muted-foreground">
          {showOnlyTableProducts && priceTableId
            ? 'Desative o filtro ou adicione produtos à tabela'
            : searchQuery
              ? 'Tente ajustar os filtros ou termo de busca'
              : 'Cadastre produtos no módulo de catálogo'}
        </p>
      </div>
    );
  }

  return (
    // v1.2.0: Habilitado scroll horizontal interno (axis="both") + min-width no
    // container das linhas para que, em telas estreitas, as novas colunas
    // (Unidade/Mensalidade/Prazo) não fiquem ocultas — exibe scrollbar interna
    // ao invés de comprimir/cortar os campos. Mesmo padrão usado em
    // DealItemsSummary (tabela de itens do negócio).
    <ScrollArea
      className="flex-1"
      axis="both"
      preventScrollChaining
      reserveScrollbarSpace
    >
      <div className="p-3 flex flex-col gap-2 md:min-w-[820px]">
        {filteredProducts.map((product) => (
          <ProductListRow
            key={product.id}
            product={product}
            priceTablePrice={priceTableMap[product.id] ?? null}
            isInDeal={product.id in dealItemsMap}
            dealQuantity={dealItemsMap[product.id] || 0}
            onAdd={onAddItem}
            onUpdateQuantity={onUpdateItemQuantity}
            onRemove={onRemoveItem}
            currency={currency}
            useRecurringPrice={onlyRecurring}
          />
        ))}

      </div>
    </ScrollArea>
  );
}
